import React, { useEffect, useState } from "react";
import { Scissors, FileText, AlertTriangle, Undo2, CheckCircle2 } from "lucide-react";
import { Btn, Input, Modal } from "@/components/Primitives";
import { api, fmtNumber, formatApiErrorDetail, saveBlob } from "@/lib/api";
import { toast } from "sonner";

const segTxt = (seg, tag) => `${seg.length}m ⌀${seg.butt_class}mm ${(seg.colour || "").toUpperCase()} (${tag})`;

function PatternText({ p }) {
  const parts = [];
  const preLen = p.pre ? p.pre.length : 0;
  if (p.pre) parts.push(`0–${preLen}m → ${segTxt(p.pre, "off-cut, stock")}`);
  if (p.d0 > preLen + 0.005) parts.push(`${preLen}–${p.d0}m → waste`);
  const pieceLen = p.piece.actual_length || p.piece.length;
  const shortNote = p.piece.actual_length && p.piece.actual_length < p.piece.length - 0.004
    ? ` (cut at ${p.piece.actual_length}m)` : "";
  let end = Math.round((p.d0 + p.k * pieceLen) * 100) / 100;
  parts.push(`${p.d0}–${end}m → ${p.k} × ${segTxt(p.piece, "ORDER")}${shortNote}`);
  (p.extras || []).forEach((ex) => {
    const exLen = ex.actual_length || ex.length;
    const exEnd = Math.round((end + ex.k * exLen) * 100) / 100;
    const exNote = ex.actual_length && ex.actual_length < ex.length - 0.004 ? ` (cut at ${ex.actual_length}m)` : "";
    parts.push(`${end}–${exEnd}m → ${ex.k} × ${segTxt(ex, "ORDER")}${exNote}`);
    end = exEnd;
  });
  if (p.offcut) {
    const offEnd = Math.round((end + p.offcut.length) * 100) / 100;
    parts.push(`${end}–${offEnd}m → ${segTxt(p.offcut, "off-cut, stock")}`);
    end = offEnd;
  }
  if ((p.waste_m || 0) > 0.005) parts.push(`waste ${p.waste_m}m`);
  return <span className="font-mono text-xs">{parts.join("  ·  ")}</span>;
}

export function CutPlanModal({ order, onClose, onApplied, initialSpecs }) {
  const standalone = !order;
  const [plan, setPlan] = useState(null);
  const [settings, setSettings] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedPlans, setAppliedPlans] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [donorLens, setDonorLens] = useState([]);
  const [includeGerman, setIncludeGerman] = useState(false);
  const [recordActual, setRecordActual] = useState([]);

  const specKey = (L, b) => `${Number(L)}|${Number(b)}`;
  const shortSpecKeys = () => {
    const out = new Set();
    (plan?.lines || []).forEach((ln) => (ln.groups || []).forEach((g) => {
      const pc = g.pattern.piece;
      if (pc.actual_length && pc.actual_length < pc.length - 0.004) out.add(specKey(pc.length, pc.butt_class));
      (g.pattern.extras || []).forEach((ex) => {
        if (ex.actual_length && ex.actual_length < ex.length - 0.004) out.add(specKey(ex.length, ex.butt_class));
      });
    }));
    return out;
  };
  const [specs, setSpecs] = useState(
    initialSpecs?.length
      ? initialSpecs.map((s) => ({ length: `${s.length ?? ""}`, butt_size: `${s.butt_size ?? ""}`, qty: `${s.qty ?? ""}`, colour: s.colour || "", note: s.note || "" }))
      : [{ length: "", butt_size: "", qty: "", colour: "", note: "" }]);

  const specLines = (sp = specs) => sp
    .map((s) => ({ length: Number(s.length), butt_size: Number(s.butt_size), qty: Math.floor(Number(s.qty)), colour: (s.colour || "").trim() || undefined, note: (s.note || "").trim() || undefined }))
    .filter((s) => s.length > 0 && s.butt_size > 0 && s.qty > 0);

  const loadApplied = () => {
    api.get("/orders/cut-plans", { params: standalone ? { standalone: true, status: "applied" } : { order_id: order.id, status: "applied" } })
      .then((r) => setAppliedPlans(r.data)).catch(() => {});
  };

  const cancelPlan = async (p) => {
    if (!window.confirm(`Cancel cut plan ${p.cut_ref}? Its ${p.segments_created} Awaiting-Paint segment line(s) will be removed and the ${p.poles_cut} donor pole(s) restored to Available.`)) return;
    setCancelling(p.id);
    try {
      const r = await api.post(`/orders/cut-plans/${p.id}/cancel`);
      toast.success(`Cut plan cancelled — ${r.data.restored_poles} donor pole(s) restored to Available`);
      loadApplied();
      loadPlan();
      onApplied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setCancelling(null); }
  };

  const ovKey = (spec) => `${spec.length}|${spec.butt_size}`;
  const ovList = (ov) => Object.entries(ov)
    .filter(([, v]) => v !== "" && v != null && !Number.isNaN(Number(v)))
    .map(([k, v]) => ({ length: Number(k.split("|")[0]), butt_size: Number(k.split("|")[1]), qty: Number(v) }));

  const loadPlan = async (ov = overrides, dl = donorLens, ig = includeGerman) => {
    if (standalone && specLines().length === 0) {
      setPlan(null);
      if (!settings) api.get("/stock/cut-settings").then((r) => setSettings(r.data)).catch(() => {});
      return;
    }
    setBusy(true);
    try {
      const body = standalone
        ? { lines: specLines(), overrides: ovList(ov), donor_lengths: dl, include_german: ig }
        : { overrides: ovList(ov), donor_lengths: dl, include_german: ig };
      const url = standalone ? "/orders/standalone-cut-plan" : `/orders/${order.id}/cut-plan`;
      const [p, st] = await Promise.all([api.post(url, body), api.get("/stock/cut-settings")]);
      setPlan(p.data);
      setSettings(st.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    loadApplied();
    if (standalone) {
      api.get("/stock/cut-settings").then((r) => setSettings(r.data)).catch(() => {});
      if (initialSpecs?.length) loadPlan();
    } else loadPlan();
    /* eslint-disable-next-line */
  }, []);

  const saveSettings = async () => {
    try {
      const sl = settings.standard_lengths;
      await api.put("/stock/cut-settings", {
        taper_mm_per_m: settings.taper_mm_per_m,
        min_offcut_m: settings.min_offcut_m,
        length_tol_m: settings.length_tol_m,
        standard_lengths: Array.isArray(sl) ? sl : String(sl).split(/[,\s]+/).filter(Boolean).map(Number),
      });
      toast.success("Cut settings saved — recalculating plan");
      loadPlan();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const url = standalone ? "/orders/standalone-cut-plan.pdf" : `/orders/${order.id}/cut-plan.pdf`;
      const body = standalone
        ? { lines: specLines(), overrides: ovList(overrides), donor_lengths: donorLens, include_german: includeGerman }
        : { overrides: ovList(overrides), donor_lengths: donorLens, include_german: includeGerman };
      const r = await api.post(url, body, { responseType: "blob" });
      saveBlob(r, `cut-plan-${standalone ? "stock" : order.order_number}.pdf`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate cut sheet"); }
  };

  const apply = async () => {
    const t = plan?.totals || {};
    const label = standalone ? "stock piece(s)" : "piece(s) for the order";
    if (!window.confirm(`Apply this cut plan? ${t.poles_to_cut} pole(s) will be cut: ${t.pieces_for_order} ${label} + ${t.offcuts} off-cut(s) go to Awaiting Paint, waste ${t.waste_m}m. The cut sheet PDF will download first.`)) return;
    setApplying(true);
    try {
      await downloadPdf();
      const url = standalone ? "/orders/standalone-cut-plan/apply" : `/orders/${order.id}/cut-plan/apply`;
      const body = {
        ...(standalone ? { lines: specLines(), overrides: ovList(overrides) } : { overrides: ovList(overrides) }),
        donor_lengths: donorLens,
        include_german: includeGerman,
        record_actual: recordActual.map((k) => k.split("|").map(Number)),
        expected: { poles_to_cut: t.poles_to_cut, pieces_for_order: t.pieces_for_order },
      };
      const r = await api.post(url, body);
      toast.success(`Cut plan applied — ${r.data.poles_cut} pole(s) cut; ${r.data.pieces_for_order} ${standalone ? "stock" : "order"} piece(s) + ${r.data.offcuts} off-cut(s) now in Awaiting Paint`);
      onApplied?.();
      if (standalone) { onClose?.(); return; }
      loadApplied();
      loadPlan();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      if (e.response?.status === 409) loadPlan();
    } finally { setApplying(false); }
  };

  const totals = plan?.totals || {};
  const canApply = (totals.poles_to_cut || 0) > 0;

  return (
    <Modal open onClose={onClose} size="xl"
      title={standalone ? "Stock Cut Plan — not linked to an order" : `Cut Plan · ${order.order_number}`}
      testid="cut-plan-modal"
      footer={<>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        <Btn variant="secondary" onClick={downloadPdf} disabled={!canApply} testid="cut-plan-pdf">
          <FileText className="h-4 w-4" /> Cut Sheet (PDF)
        </Btn>
        <Btn onClick={apply} disabled={!canApply || applying} testid="cut-plan-apply">
          <Scissors className="h-4 w-4" /> {applying ? "Applying…" : `Apply Cut Plan (${totals.poles_to_cut || 0} poles)`}
        </Btn>
      </>}>
      {standalone && (
        <div className="panel p-3 mb-4 border-teal-200 bg-teal-50/40" data-testid="standalone-cut-specs">
          <div className="text-sm font-semibold mb-2">What do you need cut? (pieces go to stock, not an order)</div>
          {specs.map((s, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2 mb-2" data-testid={`cut-spec-row-${i}`}>
              <div>
                <div className="data-label mb-1">Length (m)</div>
                <Input type="number" step="0.1" className="w-24" value={s.length}
                  onChange={(e) => setSpecs(specs.map((x, xi) => xi === i ? { ...x, length: e.target.value } : x))}
                  data-testid={`cut-spec-length-${i}`} />
              </div>
              <div>
                <div className="data-label mb-1">Butt Ø (mm)</div>
                <Input type="number" step="10" className="w-24" value={s.butt_size}
                  onChange={(e) => setSpecs(specs.map((x, xi) => xi === i ? { ...x, butt_size: e.target.value } : x))}
                  data-testid={`cut-spec-butt-${i}`} />
              </div>
              <div>
                <div className="data-label mb-1">Pieces</div>
                <Input type="number" min="1" className="w-24" value={s.qty}
                  onChange={(e) => setSpecs(specs.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x))}
                  data-testid={`cut-spec-qty-${i}`} />
              </div>
              <div>
                <div className="data-label mb-1">Colour (optional)</div>
                <Input className="w-28" value={s.colour} placeholder="auto"
                  onChange={(e) => setSpecs(specs.map((x, xi) => xi === i ? { ...x, colour: e.target.value } : x))}
                  data-testid={`cut-spec-colour-${i}`} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="data-label mb-1">Comment / instruction (shows on the PDF)</div>
                <Input value={s.note} placeholder="e.g. point these after cutting"
                  onChange={(e) => setSpecs(specs.map((x, xi) => xi === i ? { ...x, note: e.target.value } : x))}
                  data-testid={`cut-spec-note-${i}`} />
              </div>
              {specs.length > 1 && (
                <button type="button" className="text-xs text-red-600 hover:underline mb-2"
                  onClick={() => setSpecs(specs.filter((_, xi) => xi !== i))}
                  data-testid={`cut-spec-remove-${i}`}>Remove</button>
              )}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <button type="button" className="text-xs text-emerald-700 hover:underline"
              onClick={() => setSpecs([...specs, { length: "", butt_size: "", qty: "", colour: "", note: "" }])}
              data-testid="cut-spec-add">+ Add another size</button>
            <Btn size="sm" onClick={() => loadPlan()} disabled={specLines().length === 0 || busy}
                 testid="cut-spec-calc"><Scissors className="h-3.5 w-3.5" /> Calculate Cut Plan</Btn>
            <span className="text-xs text-stone-500">Butt class tolerance ±10 mm; taper &amp; off-cut rules below.</span>
          </div>
        </div>
      )}

      {appliedPlans.length > 0 && (
        <div className="panel p-3 mb-4 border-amber-200 bg-amber-50/50" data-testid="applied-cut-plans">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Scissors className="h-3.5 w-3.5 text-amber-700" /> {standalone ? "Applied stock cut plans" : "Applied cut plans on this order"}
          </div>
          {appliedPlans.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 text-xs py-1.5 border-t border-amber-100 first:border-t-0"
                 data-testid={`applied-plan-${p.id}`}>
              <span className="font-mono font-semibold">{p.cut_ref}</span>
              <span className="text-stone-500">{(p.applied_at || "").slice(0, 10)}</span>
              <span>{p.poles_cut} pole(s) cut → {p.pieces_for_order} order piece(s) + {p.offcuts} off-cut(s)</span>
              {p.cancellable ? (
                <button onClick={() => cancelPlan(p)} disabled={cancelling === p.id}
                        className="ml-auto inline-flex items-center gap-1 rounded-sm border border-red-300 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-red-700 hover:bg-red-50 disabled:opacity-40"
                        data-testid={`cancel-cut-plan-${p.id}`}>
                  <Undo2 className="h-3 w-3" /> {cancelling === p.id ? "Cancelling…" : "Cancel Cut Plan"}
                </button>
              ) : (
                <span className="ml-auto text-stone-500 italic" data-testid={`plan-locked-${p.id}`}>
                  {p.segments_moved} segment(s) already painted/allocated — can no longer be cancelled
                </span>
              )}
            </div>
          ))}
          <div className="text-[11px] text-stone-500 mt-2">
            Cancelling removes the plan's Awaiting-Paint segments and puts the donor poles back into Available stock.
          </div>
        </div>
      )}


      {settings && (
        <div className="flex flex-wrap items-end gap-3 mb-4 panel p-3 bg-stone-50/60">
          <div>
            <div className="data-label mb-1">Taper (mm per metre)</div>
            <Input type="number" step="0.1" className="w-28" value={settings.taper_mm_per_m}
              onChange={(e) => setSettings({ ...settings, taper_mm_per_m: e.target.value })} data-testid="cut-taper-input" />
          </div>
          <div>
            <div className="data-label mb-1">Min usable off-cut (m)</div>
            <Input type="number" step="0.1" className="w-28" value={settings.min_offcut_m}
              onChange={(e) => setSettings({ ...settings, min_offcut_m: e.target.value })} data-testid="cut-minoffcut-input" />
          </div>
          <div>
            <div className="data-label mb-1">Length tolerance (m)</div>
            <Input type="number" step="0.01" className="w-28" value={settings.length_tol_m ?? 0.1}
              onChange={(e) => setSettings({ ...settings, length_tol_m: e.target.value })} data-testid="cut-lengthtol-input" />
          </div>
          <Btn variant="secondary" size="sm" onClick={saveSettings} testid="cut-settings-save">Save &amp; Recalculate</Btn>
          <div className="w-full">
            <div className="data-label mb-1">Standard stock lengths (m) — off-cuts are trimmed to these</div>
            <Input className="w-full max-w-xl"
              value={Array.isArray(settings.standard_lengths) ? settings.standard_lengths.join(", ") : (settings.standard_lengths ?? "")}
              onChange={(e) => setSettings({ ...settings, standard_lengths: e.target.value })}
              data-testid="cut-stdlengths-input" />
          </div>
          <div className="text-xs text-stone-500">Butt Ø shrinks by the taper per metre from the butt end. Off-cuts are cut down to the nearest standard length (the trim is waste); anything below the minimum counts as waste.</div>
          <label className="w-full flex items-center gap-2 text-xs text-stone-600 cursor-pointer" data-testid="include-german-toggle">
            <input type="checkbox" checked={includeGerman}
              onChange={(e) => { setIncludeGerman(e.target.checked); loadPlan(overrides, donorLens, e.target.checked); }} />
            <span>Allow <b>German poles</b> as donors <span className="text-stone-400">— bundled and pointed stock is never used for cutting; German poles are excluded unless ticked.</span></span>
          </label>
          {(plan?.donor_length_options || []).length > 0 && (
            <div className="w-full flex flex-wrap items-center gap-1.5" data-testid="donor-length-filter">
              <span className="data-label mr-1">Donor pole length:</span>
              <button type="button"
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${donorLens.length === 0 ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-stone-300 hover:border-emerald-500"}`}
                onClick={() => { setDonorLens([]); loadPlan(overrides, []); }}
                data-testid="donor-length-any">Any</button>
              {plan.donor_length_options.map((L) => {
                const on = donorLens.includes(L);
                return (
                  <button key={L} type="button"
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-stone-300 hover:border-emerald-500"}`}
                    onClick={() => {
                      const next = on ? donorLens.filter((x) => x !== L) : [...donorLens, L];
                      setDonorLens(next);
                      loadPlan(overrides, next);
                    }}
                    data-testid={`donor-length-${L}`}>{L} m</button>
                );
              })}
              <span className="text-xs text-stone-400">Only these lengths are used as donors — leave on “Any” for the best overall plan.</span>
            </div>
          )}
        </div>
      )}

      {busy && <div className="text-sm text-stone-400 py-6">Calculating optimal cut plan…</div>}

      {!busy && standalone && !plan && (
        <div className="text-sm text-stone-500 py-4" data-testid="cut-plan-setup-hint">
          Enter the sizes you need above and hit <b>Calculate Cut Plan</b> — the optimizer picks the best available long poles to cut.
        </div>
      )}

      {!busy && plan && plan.lines.filter((l) => l.max_cut > 0 || l.to_cut > 0 || l.groups.length).length === 0 && (
        <div className="text-sm text-stone-500 py-6" data-testid="cut-plan-nothing">
          No shortage to cover by cutting — every line is covered by committed or directly available stock.
        </div>
      )}

      {!busy && plan && plan.lines.map((ln, li) => (
        (ln.max_cut > 0 || ln.to_cut > 0 || ln.groups.length > 0) && (
          <div key={li} className="mb-4" data-testid={`cut-plan-line-${li}`}>
            <div className="font-semibold text-sm mb-1 flex flex-wrap items-center gap-2">
              <span>Cut</span>
              <Input
                type="number" min="0" max={ln.max_cut}
                className="w-20 h-7 text-center font-mono"
                value={overrides[ovKey(ln.spec)] ?? ln.to_cut}
                onChange={(e) => setOverrides((o) => ({ ...o, [ovKey(ln.spec)]: e.target.value }))}
                onBlur={() => loadPlan()}
                onKeyDown={(e) => { if (e.key === "Enter") loadPlan(); }}
                data-testid={`cut-qty-${li}`}
              />
              <span>of <span className="font-mono">{fmtNumber(ln.max_cut)}</span> {standalone ? "requested" : "short"} × {ln.spec.length}m ⌀{ln.spec.butt_size}mm {(ln.spec.colour || "").toUpperCase()}</span>
              <span className="text-xs text-stone-500 font-normal">
                {standalone
                  ? <>requested {fmtNumber(ln.required)} · already available uncut {fmtNumber(ln.direct_available)}</>
                  : <>required {fmtNumber(ln.required)} · committed {fmtNumber(ln.committed)} · already available {fmtNumber(ln.direct_available)}</>}
                {ln.from_other_donors > 0 && <> · <span className="text-emerald-700 font-semibold">+{fmtNumber(ln.from_other_donors)} cut on other lines' donors</span></>}
              </span>
              {ln.spec.note && (
                <span className="text-xs font-normal italic text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-2 py-0.5" data-testid={`cut-line-note-${li}`}>
                  “{ln.spec.note}”
                </span>
              )}
              {shortSpecKeys().has(specKey(ln.spec.length, ln.spec.butt_size)) && (
                <label className="text-xs font-normal flex items-center gap-1 text-amber-800 cursor-pointer" data-testid={`record-actual-${li}`}>
                  <input type="checkbox"
                    checked={recordActual.includes(specKey(ln.spec.length, ln.spec.butt_size))}
                    onChange={(e) => {
                      const k = specKey(ln.spec.length, ln.spec.butt_size);
                      setRecordActual(e.target.checked ? [...recordActual, k] : recordActual.filter((x) => x !== k));
                    }} />
                  record short-cuts at actual length (else recorded as {ln.spec.length}m)
                </label>
              )}
            </div>
            {ln.to_cut === 0 ? (
              <div className="text-xs text-stone-500" data-testid={`cut-plan-skipped-${li}`}>
                Skipped — waiting for incoming stock? Enter a quantity above to plan cuts for this size.
              </div>
            ) : ln.groups.length === 0 ? (
              ln.shortfall > 0 ? (
                <div className="text-xs text-red-600 font-semibold flex items-center gap-1" data-testid={`cut-plan-no-donors-${li}`}>
                  <AlertTriangle className="h-3.5 w-3.5" /> No suitable longer poles available to cut{ln.from_other_donors > 0 ? ` — still ${fmtNumber(ln.shortfall)} piece(s) short after mixed cuts on other lines' donors` : ""}.
                </div>
              ) : (
                <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid={`cut-plan-covered-${li}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Fully covered — all {fmtNumber(ln.from_other_donors)} piece(s) are cut as part of other lines' donor patterns above. No separate donors needed.
                </div>
              )
            ) : (
              <div className="panel overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-teal-800 text-white text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Cut</th>
                      <th className="px-3 py-2 text-left">Donor poles</th>
                      <th className="px-3 py-2 text-left">Cut pattern (from butt end)</th>
                      <th className="px-3 py-2 text-right">Waste / pole</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ln.groups.map((g, gi) => {
                      const refs = g.rows.map((r) => (r.use > 1 ? `${r.bundle_number || r.id.slice(0, 8)}×${r.use}` : (r.bundle_number || r.id.slice(0, 8))));
                      const shown = refs.slice(0, 12);
                      return (
                        <tr key={gi} className="border-b border-stone-100 align-top" data-testid={`cut-plan-group-${li}-${gi}`}>
                          <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{fmtNumber(g.count)} ×</td>
                          <td className="px-3 py-2">
                            <div className="font-mono font-semibold">{g.donor.length}m ⌀{g.donor.butt_size}mm {(g.donor.colour || "").toUpperCase()}</div>
                            <div className="text-xs text-stone-500 font-mono">{shown.join(", ")}{refs.length > 12 ? ` … +${refs.length - 12} more` : ""}</div>
                          </td>
                          <td className="px-3 py-2"><PatternText p={g.pattern} /></td>
                          <td className="px-3 py-2 text-right font-mono">{g.pattern.waste_m} m</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {ln.shortfall > 0 && ln.groups.length > 0 && (
              <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1" data-testid={`cut-plan-shortfall-${li}`}>
                <AlertTriangle className="h-3.5 w-3.5" /> Still {fmtNumber(ln.shortfall)} piece(s) short — not enough suitable stock to cut.
              </div>
            )}
          </div>
        )
      ))}

      {!busy && plan && canApply && (
        <div className="panel p-3 bg-teal-50/50 border-teal-200 text-sm flex flex-wrap gap-4" data-testid="cut-plan-totals">
          <div>Cut <span className="font-mono font-bold">{fmtNumber(totals.poles_to_cut)}</span> pole(s)</div>
          <div>→ <span className="font-mono font-bold">{fmtNumber(totals.pieces_for_order)}</span> {standalone ? "stock piece(s)" : "piece(s) for the order"}</div>
          <div>+ <span className="font-mono font-bold">{fmtNumber(totals.offcuts)}</span> off-cut(s) back to stock</div>
          <div>Waste <span className="font-mono font-bold">{totals.waste_m} m</span></div>
          <div className="text-xs text-stone-500 self-center">All segments go to Awaiting Paint in their new colour — allocate the order pieces after painting.</div>
        </div>
      )}
    </Modal>
  );
}
